import { useCurrentSchoolId } from '@/hooks'
import {
  lastSetXTokenTimeStorage,
  reflectionStorage,
  store,
  xRbacTokenStorage,
} from '@/store'
import { getTheme } from '@/utils/theme'
import { Form, FormItem, Input, Password, Submit } from '@formily/antd'
import { createForm } from '@formily/core'
import { createSchemaField } from '@formily/react'
import { moment } from '@seiue/moment'
import { Toast } from '@seiue/ui'
import {
  LoginReq,
  User,
  UserDomainEnum,
  authApi,
} from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'
import { getUrlParams } from 'packages/utils/utils'
import React, { useEffect } from 'react'
import styled from 'styled-components'

import { VerifyCode } from '../VerifyCode'
import { getFormalUserJumpUrl } from '../user/utils'

import { useNormalSchema } from './schema'

const form = createForm({
  validateFirst: true,
})

const SchemaField = createSchemaField({
  components: {
    FormItem,
    Input,
    Password,
    VerifyCode,
  },
})

/**
 * 密码登录
 *
 * @param param - 参数
 * @param param.isPreview - 是否是预览
 * @param param.activeKey - 活动键
 * @param param.changePassword - 是否改密码
 * @param param.setIsSecondCheck - 设置是否是二次验证
 * @param param.setUser - 设置用户
 * @returns 组件
 */
export const PwdLogin: React.FC<{
  isPreview?: boolean
  activeKey: string
  changePassword: () => void
  setIsSecondCheck: () => void
  setUser: (user: User) => void
}> = ({ isPreview, activeKey, changePassword, setIsSecondCheck, setUser }) => {
  const theme = getTheme()

  const schoolId = useCurrentSchoolId()
  const phoneSchema = useNormalSchema(isPreview)

  const openLogin = {
    clientId: getUrlParams('client_id'),
    scope: getUrlParams('scope'),
    state: getUrlParams('state'),
    redirectUri: getUrlParams('redirect_uri'),
    responseType: getUrlParams('response_type'),
    nonce: getUrlParams('nonce'),
  }

  useEffect(() => {
    form.reset()
  }, [activeKey])

  return (
    <Form
      form={form}
      layout="vertical"
      size="large"
      onAutoSubmit={async (val: LoginReq) => {
        if (isPreview || !val.account || !val.password) {
          return
        }

        try {
          const { data: reflection } = await authApi.login(
            UserDomainEnum.Formal,
            {
              ...val,
              schoolId: Number(schoolId),
              openLogin,
            },
            { omitAuthorizationHeader: true }
          )

          if (reflection.openMsgCheck) {
            setUser(reflection)
            setIsSecondCheck()
            return
          }

          store.set(reflectionStorage, reflection)

          authApi
            .checkDataToken()
            .then(({ data }) => {
              store.set(xRbacTokenStorage, data)
              store.set(lastSetXTokenTimeStorage, moment().toISOString())
            })
            // 不管 checkDataToken 是否成功，都要重定向
            .finally(() => {
              // 不管 checkDataToken 是否成功，都进行下一步界面，不影响主流程
              if (reflection.redirectUri) {
                location.href = reflection.redirectUri
                return
              }

              location.href = getFormalUserJumpUrl(reflection.role)
            })
        } catch (e: AnyType) {
          const { data } = e.response
          Toast.error(data?.message)
        }
      }}
      style={{
        marginTop: '16px',
      }}
    >
      <ItemWrapper>
        <div>
          <SchemaField schema={phoneSchema} />
          <ForgetPassword onClick={() => !isPreview && changePassword()}>
            忘记密码
          </ForgetPassword>
        </div>
        <Submit
          block={true}
          size="large"
          style={{
            borderRadius: 8,
            backgroundColor: theme.brand._1,
            color: '#fff',
          }}
        >
          {'登录'}
        </Submit>
      </ItemWrapper>
    </Form>
  )
}

const ItemWrapper = styled.div`
  height: 250px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const ForgetPassword = styled.div`
  float: right;
  height: 20px;
  font-size: 12px;
  line-height: 20px;
  color: ${(props) => props.theme.text._3};
  cursor: pointer;
  &:hover {
    color: ${(props) => props.theme.brand._1};
  }
`
